import React from 'react';
import { Box, Input, Divider } from '@mui/material';
import { useFormikContext } from 'formik';
import { format } from 'date-fns';
import appTheme from '../../../../constants/theme';
import InputWrapper from '../InputWrapper';
import {
  buildBirthdayIsoString,
  digitsOnly,
  parseBirthdayValue,
} from '../../../../utils/birthday';

interface BirthdayInputProps {
  name: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
}

const inputStyle: React.CSSProperties = {
  border: 'none',
  padding: 0,
  height: '44px',
  textAlign: 'center',
  fontSize: appTheme.typography.body2.fontSize,
};

function BirthdayInput({
  name,
  label,
  disabled,
  required,
}: BirthdayInputProps) {
  const formik = useFormikContext();

  const formikValueDate = (formik.values as any)[name] as string;
  const initialDate = parseBirthdayValue(formikValueDate) ?? new Date();

  const [month, setMonth] = React.useState<string>(format(initialDate, 'MM'));
  const [day, setDay] = React.useState<string>(format(initialDate, 'dd'));
  const [year, setYear] = React.useState<string>(format(initialDate, 'yyyy'));

  const setFormikValue = (
    newMonth: string | undefined,
    newYear: string | undefined,
    newDay: string | undefined,
  ) => {
    const date = buildBirthdayIsoString(newMonth, newYear, newDay);
    formik.setFieldValue(name, date);
    if (date) {
      formik.setFieldTouched(name, true, true);
    }
  };

  const changeMonth = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newMonth = digitsOnly(event.target.value);
    setMonth(newMonth);
    setFormikValue(newMonth, year, day);
  };

  const changeYear = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newYear = digitsOnly(event.target.value);
    setYear(newYear);
    setFormikValue(month, newYear, day);
  };

  const changeDay = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newDay = digitsOnly(event.target.value);
    setDay(newDay);
    setFormikValue(month, year, newDay);
  };

  return (
    <InputWrapper label={label} name={name} required={required}>
      <Box
        display="flex"
        flexDirection="row"
        gap={0}
        width="100%"
        height="44px"
        boxSizing="border-box"
        border={theme => `0.5px solid ${theme.palette.lightOutlineGrey.main}`}
        borderRadius="10px"
        sx={theme => ({
          '&:focus-within': {
            borderColor: 'transparent',
            outline: `1.5px solid ${theme.palette.darkGrey.main}`,
          },
          '.MuiInputBase-root::before': {
            display: 'none',
          },
          '.MuiInputBase-root::after': {
            display: 'none',
          },
        })}>
        <Input
          disabled={disabled}
          sx={{
            width: '33%',
            fontSize: 'body2.fontSize',
          }}
          type="text"
          placeholder="MM"
          value={month}
          onChange={changeMonth}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 2,
            style: inputStyle,
          }}
        />
        <Divider
          orientation="vertical"
          component="div"
          sx={theme => ({
            borderColor: theme.palette.lightOutlineGrey.main,
            borderWidth: '0.5px',
            borderLeft: 'none',
          })}
        />
        <Input
          disabled={disabled}
          sx={{
            width: '33%',
            fontSize: 'body2.fontSize',
          }}
          type="text"
          placeholder="DD"
          value={day}
          onChange={changeDay}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 2,
            style: inputStyle,
          }}
        />
        <Divider
          orientation="vertical"
          component="div"
          sx={theme => ({
            borderColor: theme.palette.lightOutlineGrey.main,
            borderWidth: '0.5px',
            borderLeft: 'none',
          })}
        />
        <Input
          disabled={disabled}
          sx={{
            width: '33%',
            fontSize: 'body2.fontSize',
          }}
          type="text"
          placeholder="YYYY"
          value={year}
          onChange={changeYear}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 4,
            minLength: 4,
            style: inputStyle,
          }}
        />
      </Box>
    </InputWrapper>
  );
}

export default BirthdayInput;
